import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs';

/**
 * Query string (starting with '?', or empty) carrying an item-detail scope —
 * for building links into the item detail pages that preserve a period range
 * and site selection. Null/absent values are omitted, leaving the detail page
 * on its backend-default window.
 */
export function buildItemScopeSearch(scope: {
  periodStart?: string | null;
  periodEnd?: string | null;
  sites?: string[];
}): string {
  const params = new URLSearchParams();
  if (scope.periodStart) params.set('periodStart', scope.periodStart);
  if (scope.periodEnd) params.set('periodEnd', scope.periodEnd);
  if (scope.sites?.length) {
    // nuqs's parseAsArrayOf reads a single comma-separated param, not
    // repeated site= entries.
    params.set('site', scope.sites.join(','));
  }
  const search = params.toString();
  return search ? `?${search}` : '';
}

/**
 * Page-level scope for the purchased-good item detail pages: an explicit
 * time-period range plus a site filter, shared by every tab via the URL.
 * Null period boundaries mean "no explicit selection" — requests go without
 * period params and the backend resolves its default window (first purchase
 * month → current month), echoing the applied boundaries back.
 *
 * The param names match the purchasing insights page, so a drill-through
 * from a filtered view carries its scope with no translation.
 */
export function useItemScope() {
  const [scope, setScope] = useQueryStates({
    periodStart: parseAsString,
    periodEnd: parseAsString,
    site: parseAsArrayOf(parseAsString).withDefault([]),
  });

  const setPeriodRange = (start: string | null, end: string | null) =>
    setScope({ periodStart: start, periodEnd: end });

  const setSites = (sites: string[]) =>
    setScope({ site: sites.length ? sites : null });

  /**
   * Query string (starting with '?', or empty) carrying the current scope,
   * for building tab links that preserve it.
   */
  const scopeSearch = buildItemScopeSearch({
    periodStart: scope.periodStart,
    periodEnd: scope.periodEnd,
    sites: scope.site,
  });

  // A single boundary is valid — the backend completes the window from that
  // period's own reporting year.
  const rangeParams = {
    ...(scope.periodStart ? { timePeriodStartUuid: scope.periodStart } : {}),
    ...(scope.periodEnd ? { timePeriodEndUuid: scope.periodEnd } : {}),
  };

  /** Params for the per-item insights endpoint. */
  const insightsParams = {
    ...rangeParams,
    ...(scope.site.length ? { site: scope.site } : {}),
  };

  /** Params for the records and spend endpoints (plural site naming). */
  const recordsParams = {
    ...rangeParams,
    ...(scope.site.length ? { sites: scope.site } : {}),
  };

  return {
    periodStart: scope.periodStart,
    periodEnd: scope.periodEnd,
    sites: scope.site,
    setPeriodRange,
    setSites,
    scopeSearch,
    insightsParams,
    recordsParams,
  };
}
